import React from "react"
import styled from "styled-components"
import { useIntl } from "gatsby-plugin-react-intl"

import Layout from "./Layout"
import Hero from "../components/Hero"
import Cta from "../components/Cta"

import Snippet from "../components/Snippet"

import CrossNav from "../components/industries/CrossNav"
import YouTubePlayer from "../components/YouTubePlayer"
import ImageContentful from "../components/ImageContentful"
import Lightbox from "../components/Lightbox"
import LogoRow, { Header as LogoRowHeader, Logos } from "../components/LogoRow"

import { media } from "../styles"
import playIcon from "../images/icons/play--sun.svg"

const HeroBackground = styled.div`
  color: ${({ theme }) => theme.greyWarm};
  display: none;
  position: absolute;
  left: 0;
  bottom: -25px;
  width: 390px;

  @media ${media.lg} {
    display: block;
  }

  svg {
    display: block;
    height: auto;
    width: 100%;
  }
`

const HeroContent = styled.div`
  margin: -170px 20px 30px;
  position: relative;

  @media ${media.md} {
    margin: -30px auto 30px;
    width: 300px;
  }

  @media ${media.lg} {
    margin: 0 auto;
    width: 400px;
  }

  @media ${media.xl} {
    margin: 0 auto;
    width: 600px;
  }
`

const VideoThumbnail = styled.div`
  cursor: pointer;
  display: flex;
  justify-content: center;

  &:after {
    background: url(${playIcon}) center center no-repeat;
    background-size: 100% auto;
    content: "";
    display: block;
    height: 50px;
    left: 50%;
    pointer-events: none;
    position: absolute;
    top: 50%;
    transform: translate3d(-50%, -50%, 0);
    transition: transform ${({ theme }) => theme.transitionTime};
    width: 50px;
    z-index: 3;

    @media ${media.md} {
      height: 64px;
      width: 64px;
    }
  }

  &:hover {
    &:after {
      transform: translate3d(-50%, -50%, 0) scale(1.2);
    }
  }
`

const HeroImage = styled.div`
  pointer-events: none;
  position: relative;
  user-select: none;

  @media ${media.xl} {
    height: 500px;
    margin: -20px auto 0;
  }
`

const HeroImageContainer = styled.div`
  width: 100%;

  @media ${media.lg} {
    position: absolute;
    top: 0;
    bottom: 0;
  }
`

const PartnerLogos = styled(LogoRow)`
  margin: 30px 0 40px;

  ${LogoRowHeader} {
    margin-bottom: 5px;
  }

  @media ${media.lgDown} {
    ${Logos} {
      justify-content: center;
    }

    ${LogoRowHeader} {
      text-align: center;
    }
  }
`

const HeroContentLeft = ({
  heroVideoThumbnail,
  heroFullsizeImage,
  youTubeVideoUrl,
}) => {
  const [showDialog, setShowDialog] = React.useState(false)

  const image = youTubeVideoUrl ? heroVideoThumbnail : heroFullsizeImage

  const openLightbox = (e) => {
    e.preventDefault()
    setShowDialog(true)
  }
  const closeLightbox = () => setShowDialog(false)

  return (
    <HeroContent>
      {youTubeVideoUrl ? (
        <>
          <VideoThumbnail
            aria-label="Video"
            onClick={(e) => openLightbox(e)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                openLightbox(e)
              }
            }}
            role="button"
            tabIndex={0}
          >
            <ImageContentful alt="Video" image={image} />
          </VideoThumbnail>

          <Lightbox show={showDialog} close={closeLightbox} ariaLabel={"Video"}>
            <YouTubePlayer
              url={youTubeVideoUrl}
              autoplay={true}
              isPlaying={true}
              thumbnail={
                <ImageContentful alt="Video" image={heroVideoThumbnail} />
              }
            />
          </Lightbox>
        </>
      ) : heroFullsizeImage ? (
        <HeroImage aria-hidden="true">
          <HeroImageContainer>
            <ImageContentful alt="" image={heroFullsizeImage} />
          </HeroImageContainer>
        </HeroImage>
      ) : null}
    </HeroContent>
  )
}

const HeroIllustration = () => {
  return (
    <HeroBackground>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="390"
        height="582"
        viewBox="0 0 390 582"
      >
        <polygon
          fill="currentColor"
          fillRule="evenodd"
          points="20 415 410 997 20 997"
          transform="translate(-20 -415)"
        />
      </svg>
    </HeroBackground>
  )
}

const IndustryPageLayout = (props) => {
  const intl = useIntl()
  const { industry } = props

  return (
    <Layout layout={props.layout} pageId={`industry/${industry.slug}`}>
      <Hero
        subHeader={industry.name}
        header={industry.header}
        text={industry.intro?.childMarkdownRemark.html}
        linkText={Snippet({ id: "servicesHeroCTA", field: "linkText" })}
        linkUrl={Snippet({ id: "servicesHeroCTA", field: "linkUrl" })}
        contentLeft={
          <HeroContentLeft
            heroFullsizeImage={industry.heroFullsizeImage}
            heroVideoThumbnail={industry.heroVideoThumbnail}
            youTubeVideoUrl={industry.youTubeVideoUrl}
          />
        }
        contentExtra={
          <>
            {industry.clients && (
              <PartnerLogos
                name={Snippet({ id: "industriesPartners", field: "header" })}
                logos={industry.clients}
              />
            )}
          </>
        }
        illustration={<HeroIllustration />}
        heroExtraButton={props.heroExtraButton}
        overrideHeight={props.heroOverrideHeight}
      />
      {props.children}

      {industry.ctaHeader && industry.ctaLinkText && (
        <Cta
          header={industry.ctaHeader}
          linkText={industry.ctaLinkText}
          linkUrl={industry.ctaLinkUrl || `/${intl.locale}/survey/`}
          highlightButton
        />
      )}
      <CrossNav current={industry.slug} />
    </Layout>
  )
}

export default IndustryPageLayout
